import NumberFormat from 'react-number-format';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { Shift, ShiftTypes } from '@/types';
import { useAuth, useShifts } from '@/hooks';
import { Error } from '@/components';
import { messages } from '@/utils/helpers';
import { SearchEmployees } from '@/pages/Employees/components';

export interface OpenShiftProps extends DialogProps {
  onClose(): void;
}

const OpenShift: React.FC<OpenShiftProps> = ({ onClose, ...props }) => {
  const { user } = useAuth();
  const { shift, openShiftLoading, isError, handleOpenShift } = useShifts();

  const { values, errors, touched, setFieldValue, handleSubmit, handleChange } = useFormik<Shift>({
    initialValues: shift
      ? { ...shift, receiving_employee: user?.employee! }
      : {
          cash_amount: 0,
          keys_count: 0,
          organization: null,
          pledges_count: 0,
          point: null,
          type: ShiftTypes.OPEN,
          passing_employee: null,
          receiving_employee: null,
        },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      cash_amount: yup.number().required(messages.form.REQUIRED),
      keys_count: yup.number().required(messages.form.REQUIRED),
      organization: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
      pledges_count: yup.number().required(messages.form.REQUIRED),
      point: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
      passing_employee: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
      receiving_employee: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
    }),
    onSubmit: handleOpenShift,
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Открыть смену</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {isError ? (
            <Error
              text="Произошла ошибка с загрузкой данных по смене! Пожалуйста, попробуйте позже"
              variant="outlined"
            />
          ) : (
            <Grid container spacing={3}>
              <Grid item lg={6} md={6} xs={12}>
                <TextField
                  name="organization"
                  value={values.organization ? values.organization?.full_name : ''}
                  label="Организация"
                  fullWidth
                  disabled={openShiftLoading}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <TextField
                  name="point"
                  value={values.point ? values.point?.name : ''}
                  label="Точка"
                  fullWidth
                  disabled={openShiftLoading}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <SearchEmployees
                  value={values.passing_employee}
                  onChange={(event, option) => setFieldValue('passing_employee', option)}
                  disabled={openShiftLoading}
                  getOptionLabel={({ name, last_name, father_name }) => `${last_name} ${name} ${father_name} `}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      name="passing_employee"
                      label="Передающий смену"
                      placeholder="Выберите из списка"
                      error={Boolean(touched.passing_employee && errors.passing_employee)}
                      helperText={touched.passing_employee && errors.passing_employee}
                    />
                  )}
                />
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <TextField
                  name="receiving_employee"
                  value={
                    values.receiving_employee
                      ? `${values.receiving_employee?.last_name} ${values.receiving_employee?.name} ${values.receiving_employee?.father_name}`
                      : ''
                  }
                  label="Принимающий смену"
                  fullWidth
                  disabled={openShiftLoading}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <NumberFormat
                  customInput={TextField}
                  readOnly
                  name="pledges_count"
                  label="Кол-во активных залогов"
                  placeholder="Введите значение"
                  value={values.pledges_count}
                  onChange={handleChange}
                  disabled={openShiftLoading}
                  error={touched.pledges_count && Boolean(errors.pledges_count)}
                  helperText={touched.pledges_count ? errors.pledges_count : ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <NumberFormat
                  customInput={TextField}
                  readOnly
                  name="cash_amount"
                  label="Касса на начало смены"
                  placeholder="Введите значение"
                  value={values.cash_amount}
                  onChange={handleChange}
                  disabled={openShiftLoading}
                  error={touched.cash_amount && Boolean(errors.cash_amount)}
                  helperText={touched.cash_amount ? errors.cash_amount : ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <NumberFormat
                  customInput={TextField}
                  name="keys_count"
                  label="Кол-во ключей"
                  placeholder="Введите значение"
                  value={values.keys_count}
                  onChange={handleChange}
                  disabled={openShiftLoading}
                  error={touched.keys_count && Boolean(errors.keys_count)}
                  helperText={touched.keys_count ? errors.keys_count : ''}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button type="button" color="secondary" variant="outlined" disabled={openShiftLoading} onClick={onClose}>
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={openShiftLoading}>
            Провести
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default OpenShift;
